import { useMemo } from "react";
import type { ChoiceValue, EvidenceItem, IntervieweeDetail } from "../../../domain/contracts";
import { humanizeCode, resolveChoiceValueLabel, resolveChoiceValueListLabels } from "../model/incidentLabeling";
import type {
  VillageIncidentChartCardData,
  VillageIncidentChartDatum,
  VillageIncidentDimensionOption,
} from "../model/villageIncidentCharts";

type TortureRecord = {
  incidentTypes: ChoiceValue[];
  perpetrators: ChoiceValue[];
  locationType: ChoiceValue | null;
  township: string | null;
  villageName: string | null;
};

type MurderedRecord = {
  relationship: ChoiceValue | null;
  gender: ChoiceValue | null;
  age: number | null;
  perpetrators: ChoiceValue[];
  locationType: ChoiceValue | null;
  township: string | null;
  villageName: string | null;
};

type MissingRecord = {
  relationship: ChoiceValue | null;
  gender: ChoiceValue | null;
  age: number | null;
  isAlive: ChoiceValue | null;
  perpetrators: ChoiceValue[];
  locationType: ChoiceValue | null;
  township: string | null;
  villageName: string | null;
};

type MassGraveRecord = {
  burialSiteType: ChoiceValue[];
  estimatedBuried: ChoiceValue | null;
  township: string | null;
  villageName: string | null;
};

type EvidenceRecord = {
  branchKind: string | null;
  slotType: string | null;
  mediaType: string;
  redactionStatus: string;
};

type DimensionExtractor<TRecord> = {
  key: string;
  label: string;
  getLabels: (record: TRecord) => string[];
};

type UseVillageIncidentAggregatesResult = {
  chartCards: VillageIncidentChartCardData[];
};

const UNKNOWN_LABEL = "Unknown";

function normalizeText(value: string | null | undefined): string {
  const text = value?.trim() ?? "";
  return text.length > 0 ? text : UNKNOWN_LABEL;
}

function ageBucket(age: number | null | undefined): string {
  if (!Number.isFinite(age)) {
    return UNKNOWN_LABEL;
  }

  if ((age ?? 0) <= 17) {
    return "0-17";
  }
  if ((age ?? 0) <= 30) {
    return "18-30";
  }
  if ((age ?? 0) <= 45) {
    return "31-45";
  }
  if ((age ?? 0) <= 60) {
    return "46-60";
  }

  return "61+";
}

function labelFromChoice(value: ChoiceValue | null | undefined, group: Parameters<typeof resolveChoiceValueLabel>[1]): string {
  if (!value) {
    return UNKNOWN_LABEL;
  }

  return resolveChoiceValueLabel(value, group);
}

function labelsFromChoiceList(values: ChoiceValue[] | null | undefined, group: Parameters<typeof resolveChoiceValueListLabels>[1]): string[] {
  if (!values || values.length === 0) {
    return [UNKNOWN_LABEL];
  }

  const labels = resolveChoiceValueListLabels(values, group).filter((label) => label.trim().length > 0);
  return labels.length > 0 ? labels : [UNKNOWN_LABEL];
}

function countByDimensions<TRecord>(records: TRecord[], extractLabels: (record: TRecord) => string[]): VillageIncidentChartDatum[] {
  const counter = new Map<string, number>();

  for (const record of records) {
    const labels = extractLabels(record)
      .map((label) => label.trim())
      .filter((label) => label.length > 0);

    const buckets = labels.length > 0 ? labels : [UNKNOWN_LABEL];

    for (const bucket of buckets) {
      counter.set(bucket, (counter.get(bucket) ?? 0) + 1);
    }
  }

  return [...counter.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));
}

function buildCard<TRecord>(
  id: string,
  title: string,
  iconPath: string,
  records: TRecord[],
  dimensions: DimensionExtractor<TRecord>[],
): VillageIncidentChartCardData | null {
  if (records.length === 0 || dimensions.length === 0) {
    return null;
  }

  const dataByDimension: Record<string, VillageIncidentChartDatum[]> = {};
  for (const dimension of dimensions) {
    dataByDimension[dimension.key] = countByDimensions(records, dimension.getLabels);
  }

  const dimensionOptions: VillageIncidentDimensionOption[] = dimensions.map((dimension) => ({
    key: dimension.key,
    label: dimension.label,
  }));

  return {
    id,
    title,
    iconPath,
    dimensions: dimensionOptions,
    defaultDimensionKey: dimensions[0].key,
    dataByDimension,
    totalCount: records.length,
  };
}

export function useVillageIncidentAggregates(
  intervieweeDetails: IntervieweeDetail[],
  evidenceItems: EvidenceItem[],
): UseVillageIncidentAggregatesResult {
  return useMemo(() => {
    const tortureRecords: TortureRecord[] = [];
    const murderedRecords: MurderedRecord[] = [];
    const missingRecords: MissingRecord[] = [];
    const massGraveRecords: MassGraveRecord[] = [];

    for (const detail of intervieweeDetails) {
      const profile = detail.profile;
      if (!profile) {
        continue;
      }

      for (const incident of profile.tortureOrRapeIncidents ?? []) {
        tortureRecords.push({
          incidentTypes: incident.incidentTypes ?? [],
          perpetrators: incident.perpetrators ?? [],
          locationType: incident.locationType ?? null,
          township: incident.township ?? null,
          villageName: incident.villageName ?? null,
        });
      }

      for (const incident of profile.murderedPersons ?? []) {
        murderedRecords.push({
          relationship: incident.relationship ?? null,
          gender: incident.gender ?? null,
          age: incident.age ?? null,
          perpetrators: incident.perpetrators ?? [],
          locationType: incident.locationType ?? null,
          township: incident.township ?? null,
          villageName: incident.villageName ?? null,
        });
      }

      for (const incident of profile.missingPersons ?? []) {
        missingRecords.push({
          relationship: incident.relationship ?? null,
          gender: incident.gender ?? null,
          age: incident.age ?? null,
          isAlive: incident.isAlive ?? null,
          perpetrators: incident.perpetrators ?? [],
          locationType: incident.locationType ?? null,
          township: incident.township ?? null,
          villageName: incident.villageName ?? null,
        });
      }

      for (const incident of profile.massGraves ?? []) {
        massGraveRecords.push({
          burialSiteType: incident.burialSiteType ?? [],
          estimatedBuried: incident.estimatedBuried ?? null,
          township: incident.township ?? null,
          villageName: incident.villageName ?? null,
        });
      }
    }

    const evidenceRecords: EvidenceRecord[] = evidenceItems.map((item) => ({
      branchKind: item.branchKind,
      slotType: item.slotType,
      mediaType: item.mediaType,
      redactionStatus: item.redactionStatus,
    }));

    const cards = [
      buildCard(
        "evidence",
        "Village Documents",
        "/evidence-signals.png",
        evidenceRecords,
        [
          {
            key: "branchKind",
            label: "Branch Kind",
            getLabels: (record) => [humanizeCode(record.branchKind ?? UNKNOWN_LABEL)],
          },
          {
            key: "slotType",
            label: "Slot Type",
            getLabels: (record) => [humanizeCode(record.slotType ?? UNKNOWN_LABEL)],
          },
          {
            key: "mediaType",
            label: "Media Type",
            getLabels: (record) => [humanizeCode(record.mediaType)],
          },
          {
            key: "redactionStatus",
            label: "Redaction Status",
            getLabels: (record) => [humanizeCode(record.redactionStatus)],
          },
        ],
      ),
      buildCard(
        "tortureOrRape",
        "Torture / Rape Incidents",
        "/torture-rape.png",
        tortureRecords,
        [
          {
            key: "incidentTypes",
            label: "Incident Type",
            getLabels: (record) => labelsFromChoiceList(record.incidentTypes, "incidentType"),
          },
          {
            key: "perpetrators",
            label: "Perpetrators",
            getLabels: (record) => labelsFromChoiceList(record.perpetrators, "perpetrator"),
          },
          {
            key: "locationType",
            label: "Incident Location",
            getLabels: (record) => [labelFromChoice(record.locationType, "locationType")],
          },
          {
            key: "township",
            label: "Township",
            getLabels: (record) => [normalizeText(record.township)],
          },
          {
            key: "village",
            label: "Village",
            getLabels: (record) => [normalizeText(record.villageName)],
          },
        ],
      ),
      buildCard(
        "murdered",
        "Murdered Persons",
        "/murdered.png",
        murderedRecords,
        [
          {
            key: "relationship",
            label: "Relationship",
            getLabels: (record) => [labelFromChoice(record.relationship, "relationship")],
          },
          {
            key: "gender",
            label: "Gender",
            getLabels: (record) => [labelFromChoice(record.gender, "gender")],
          },
          {
            key: "perpetrators",
            label: "Perpetrators",
            getLabels: (record) => labelsFromChoiceList(record.perpetrators, "perpetrator"),
          },
          {
            key: "locationType",
            label: "Incident Location",
            getLabels: (record) => [labelFromChoice(record.locationType, "locationType")],
          },
          {
            key: "township",
            label: "Township",
            getLabels: (record) => [normalizeText(record.township)],
          },
          {
            key: "village",
            label: "Village",
            getLabels: (record) => [normalizeText(record.villageName)],
          },
          {
            key: "ageBucket",
            label: "Age Bucket",
            getLabels: (record) => [ageBucket(record.age)],
          },
        ],
      ),
      buildCard(
        "missingOrAbducted",
        "Missing / Abducted Persons",
        "/missing.png",
        missingRecords,
        [
          {
            key: "relationship",
            label: "Relationship",
            getLabels: (record) => [labelFromChoice(record.relationship, "relationship")],
          },
          {
            key: "gender",
            label: "Gender",
            getLabels: (record) => [labelFromChoice(record.gender, "gender")],
          },
          {
            key: "isAlive",
            label: "Is Alive",
            getLabels: (record) => [labelFromChoice(record.isAlive, "aliveStatus")],
          },
          {
            key: "perpetrators",
            label: "Perpetrators",
            getLabels: (record) => labelsFromChoiceList(record.perpetrators, "perpetrator"),
          },
          {
            key: "locationType",
            label: "Incident Location",
            getLabels: (record) => [labelFromChoice(record.locationType, "locationType")],
          },
          {
            key: "township",
            label: "Township",
            getLabels: (record) => [normalizeText(record.township)],
          },
          {
            key: "village",
            label: "Village",
            getLabels: (record) => [normalizeText(record.villageName)],
          },
          {
            key: "ageBucket",
            label: "Age Bucket",
            getLabels: (record) => [ageBucket(record.age)],
          },
        ],
      ),
      buildCard(
        "massGrave",
        "Mass Grave Sites",
        "/mass-grave.png",
        massGraveRecords,
        [
          {
            key: "burialSiteType",
            label: "Burial Site Type",
            getLabels: (record) => labelsFromChoiceList(record.burialSiteType, "burialSiteType"),
          },
          {
            key: "estimatedBuried",
            label: "Estimated Buried",
            getLabels: (record) => [labelFromChoice(record.estimatedBuried, "estimatedBuried")],
          },
          {
            key: "township",
            label: "Township",
            getLabels: (record) => [normalizeText(record.township)],
          },
          {
            key: "village",
            label: "Village",
            getLabels: (record) => [normalizeText(record.villageName)],
          },
        ],
      ),
    ].filter((card): card is VillageIncidentChartCardData => Boolean(card));

    return { chartCards: cards };
  }, [evidenceItems, intervieweeDetails]);
}
